import { Controller } from "@hotwired/stimulus"

// Handles thumbs-up / thumbs-down feedback on coach messages.
// Posts the chosen feedback to the server, highlights the selected button,
// and reveals a thank-you note on success.
//
// View: app/views/coach/_feedback_buttons.html.erb
//   data-feedback-url-value, data-feedback-message-id-value,
//   data-feedback-current-feedback-value
//   targets: positiveButton, negativeButton, thankYou
//   each button: data-action="click->feedback#submit" data-value="positive|negative"
export default class extends Controller {
  static targets = ["positiveButton", "negativeButton", "thankYou"]
  static values = {
    url: String,
    messageId: String,
    currentFeedback: String
  }

  // Active-state classes match the inline warm-clay tokens used in the view
  // (moss = positive, ember = negative). Applied at runtime, so they are
  // invisible to the ERB brand guard — pinned by feedback_ack_contract_spec.
  static ACTIVE_CLASSES = {
    positive: ["text-moss-600", "bg-moss-100"],
    negative: ["text-ember-700", "bg-ember-100"]
  }

  connect() {
    // Reflect any previously-stored feedback by highlighting the matching button.
    if (this.hasCurrentFeedbackValue && this.currentFeedbackValue) {
      this.highlight(this.currentFeedbackValue)
    }
  }

  async submit(event) {
    const button = event.currentTarget
    // The view marks each button with a plain `data-value="positive|negative"`.
    const feedback = button.dataset.value
    if (feedback !== "positive" && feedback !== "negative") return
    if (!this.hasUrlValue || !this.urlValue) return

    this.disableButtons()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          message_id: this.messageIdValue,
          feedback: feedback
        })
      })

      if (response.ok) {
        this.currentFeedbackValue = feedback
        this.highlight(feedback)
        this.showThankYou()
      } else {
        this.enableButtons()
      }
    } catch (error) {
      this.enableButtons()
    }
  }

  // Apply the active styling to the chosen button and clear it from the other.
  highlight(feedback) {
    this.clearActive(this.positiveButtonTarget, "positive")
    this.clearActive(this.negativeButtonTarget, "negative")

    if (feedback === "positive" && this.hasPositiveButtonTarget) {
      this.positiveButtonTarget.classList.add(...this.constructor.ACTIVE_CLASSES.positive)
    } else if (feedback === "negative" && this.hasNegativeButtonTarget) {
      this.negativeButtonTarget.classList.add(...this.constructor.ACTIVE_CLASSES.negative)
    }
  }

  clearActive(target, kind) {
    if (!target) return
    target.classList.remove(...this.constructor.ACTIVE_CLASSES[kind])
  }

  showThankYou() {
    if (this.hasThankYouTarget) {
      this.thankYouTarget.classList.remove("hidden")
    }
    this.hideButtons()
  }

  hideButtons() {
    if (this.hasPositiveButtonTarget) this.positiveButtonTarget.classList.add("hidden")
    if (this.hasNegativeButtonTarget) this.negativeButtonTarget.classList.add("hidden")
  }

  disableButtons() {
    if (this.hasPositiveButtonTarget) this.positiveButtonTarget.disabled = true
    if (this.hasNegativeButtonTarget) this.negativeButtonTarget.disabled = true
  }

  enableButtons() {
    if (this.hasPositiveButtonTarget) this.positiveButtonTarget.disabled = false
    if (this.hasNegativeButtonTarget) this.negativeButtonTarget.disabled = false
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
